import React from 'react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    variant?: 'default' | 'search' | 'outline';
    label?: React.ReactNode;
    labelClassName?: string;
    error?: string;
    helperText?: string;
    /** Optional icon rendered inside the field (e.g. a lucide-preact icon) */
    icon?: React.ReactNode;
    iconPosition?: 'left' | 'right';
}

const VARIANT_CLASSES: Record<NonNullable<InputProps['variant']>, string> = {
    default:
        'w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition-all duration-200 placeholder:text-slate-400 hover:border-slate-300 focus:border-slate-400 focus:ring-2 focus:ring-slate-100',
    search: 'w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 shadow-sm outline-none transition-all duration-200 placeholder:text-slate-400 hover:border-slate-300 focus:border-slate-400 focus:ring-2 focus:ring-slate-100',
    outline:
        'w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition-all duration-200 placeholder:text-slate-400 hover:border-slate-400 focus:border-slate-500 focus:ring-2 focus:ring-slate-100',
};

const Input = React.forwardRef<HTMLInputElement, InputProps>((
    {
        className,
        variant = 'default',
        label,
        labelClassName = '',
        error,
        helperText,
        icon,
        iconPosition = 'left',
        type,
        ...props
    },
    ref,
) => {
    const inputClasses = [
        VARIANT_CLASSES[variant],
        icon && iconPosition === 'left' ? 'pl-10' : '',
        icon && iconPosition === 'right' ? 'pr-10' : '',
        error ? 'border-red-400 focus:border-red-500 focus:ring-red-100' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className="w-full">
            {label && (
                <label htmlFor={props.id} className={`mb-2 block text-sm font-medium text-slate-700 ${labelClassName}`.trim()}>
                    {label}
                </label>
            )}
            <div className="relative">
                {icon && (
                    <span
                        className={[
                            'pointer-events-none absolute top-1/2 -translate-y-1/2 text-slate-400 [&>svg]:h-4 [&>svg]:w-4',
                            iconPosition === 'left' ? 'left-3.5' : 'right-3.5',
                        ].join(' ')}
                    >
                        {icon}
                    </span>
                )}
                <input type={type} className={inputClasses} ref={ref} {...props} />
            </div>
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
            {helperText && !error && <p className="mt-1 text-sm text-slate-500">{helperText}</p>}
        </div>
    );
});

Input.displayName = 'Input';

export default Input;
